/**
 * Live trivias discovery list.
 *
 * Rules:
 *   - Live now + I'm a participant → "Enter" → /live/{id} game room
 *   - Live now + NOT a participant → button disabled (late-join lock)
 *   - Upcoming + already joined → "Joined ✓ View" → /live/{id}
 *   - Upcoming + not yet joined → "Join" calls /live/{id}/join
 *
 * Renders topic chips (from `categories` resolved server-side) + live
 * countdowns next to each scheduled trivia. The countdown ticker runs on a
 * single shared interval; only the time element re-renders so the rest of
 * the card stays stable.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtCountdown(targetIso) {
  if (!targetIso) return '';
  const ms = new Date(targetIso).getTime() - Date.now();
  if (ms <= 0) return 'starting…';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `in ${d}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  if (m > 0) return `in ${m}m ${sec}s`;
  return `in ${sec}s`;
}

let countdownTimer = null;
let listPollTimer = null;
// Track which registered trivias we've already redirected for so we don't
// loop if the user navigates back to /live after one starts.
const _autoRedirected = new Set();

function startCountdownTicker() {
  if (countdownTimer) clearInterval(countdownTimer);
  countdownTimer = setInterval(() => {
    document.querySelectorAll('[data-countdown-to]').forEach((el) => {
      el.textContent = fmtCountdown(el.dataset.countdownTo);
    });
  }, 1000);
}

function stopTimers() {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
  if (listPollTimer) { clearTimeout(listPollTimer); listPollTimer = null; }
}

export async function renderLive(root) {
  stopTimers();
  // Stop everything if the user navigates away from this page.
  window.addEventListener('popstate', stopTimers, { once: true });

  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Live trivia</div>
      <div class="appbar-spacer"></div>
      <span class="tag mag" id="live-count"><span class="dot"></span>0 LIVE</span>
    </div>
    <div class="pad" id="live-page">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const page = document.getElementById('live-page');
  await refreshAndPaint(page);
  // Background poll so the listing reflects status changes (esp. an
  // upcoming trivia transitioning to live) and the auto-redirect can fire.
  schedulePoll(page);
}

async function refreshAndPaint(page) {
  let data;
  let recent = null;
  try {
    // Fire both in parallel — the recent endpoint is cheap (one indexed row).
    [data, recent] = await Promise.all([
      api('/live'),
      api('/live/me/recent').catch(() => null),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    page.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  const live = data.live || [];
  const upcoming = data.upcoming || [];
  document.getElementById('live-count').innerHTML = `<span class="dot"></span>${live.length} LIVE`;

  // Auto-redirect: if a trivia the user is registered for has just
  // transitioned to "live now", drop them into the game room. One-shot per
  // trivia per session via _autoRedirected so coming back to /live doesn't
  // bounce them again.
  const goingLive = live.find((t) => t.is_registered && !_autoRedirected.has(t.id));
  if (goingLive) {
    _autoRedirected.add(goingLive.id);
    stopTimers();
    toast.success(`${goingLive.title} is starting — let's go!`);
    navigate(`/live/${goingLive.id}/play`);
    return;
  }

  page.innerHTML = `
    ${recent ? renderRecentCard(recent) : ''}

    <h3 class="sec"><span class="dot"></span>Live now</h3>
    ${live.length
      ? live.map(renderLiveNow).join('')
      : `<div class="card muted">Nothing live right now. Check back soon.</div>`}

    <h3 class="sec">Upcoming</h3>
    ${upcoming.length
      ? upcoming.map(renderUpcoming).join('')
      : `<div class="card muted">No scheduled trivias yet.</div>`}
  `;
  startCountdownTicker();
}

function renderRecentCard(t) {
  // The user's most-recent participation. If it's still LIVE, "Resume"
  // takes them back into the room; otherwise "View results" goes to the
  // post-game leaderboard so they can review their finish.
  const isLive = t.status === 'live';
  const cta = isLive
    ? `<a href="/live/${escape(t.id)}" data-link class="tag mag" style="text-decoration:none;font-family:var(--font-display);">
         Resume →
       </a>`
    : `<a href="/live/${escape(t.id)}/results" data-link class="tag vio" style="text-decoration:none;font-family:var(--font-display);">
         View results →
       </a>`;
  const subline = t.finished_at
    ? `Ended ${escape(new Date(t.finished_at).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' }))}`
    : (isLive ? 'In progress' : 'Recent');
  return `
    <h3 class="sec">Your last trivia</h3>
    <div class="card" style="padding:12px;border:1px solid var(--violet);">
      <div class="row">
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">${escape(t.title)}</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:1px;">
            ${subline} · ${t.participants_count} ${t.participants_count === 1 ? 'player' : 'players'}
          </div>
        </div>
        ${cta}
      </div>
    </div>
  `;
}

function schedulePoll(page) {
  // Re-fetch the list every 5s. Cheap enough at alpha scale and lets the UI
  // pick up scheduled→live transitions without manual refresh.
  if (listPollTimer) clearTimeout(listPollTimer);
  listPollTimer = setTimeout(async () => {
    await refreshAndPaint(page);
    // Keep going until the user navigates away.
    if (document.getElementById('live-page')) schedulePoll(page);
  }, 5000);
}

function topicChips(t) {
  const cats = t.categories || [];
  if (cats.length === 0) return '';
  return `
    <div class="row" style="gap:6px;flex-wrap:wrap;margin-top:8px;">
      ${cats.map((c) => `
        <span class="tag vio" style="font-size:10px;padding:2px 7px;">
          ${escape(c.name)}
        </span>
      `).join('')}
    </div>
  `;
}

function renderLiveNow(t) {
  const canEnter = t.is_registered;
  const ctaHtml = canEnter
    ? `<a href="/live/${escape(t.id)}/play" data-link class="btn gold" style="margin-bottom:2px;">Enter</a>`
    : `<button type="button" class="btn gold" disabled style="margin-bottom:2px;opacity:0.55;cursor:not-allowed;">
         Started — joining closed
       </button>`;
  return `
    <div class="hero pink">
      <div class="row">
        <span class="tag" style="background:rgba(0,0,0,0.25);color:#fff;">LIVE</span>
        <div class="sp"></div>
        <span style="font-size:11px;font-weight:500;opacity:0.95;">
          ${t.participants_count} ${t.participants_count === 1 ? 'player' : 'players'}
        </span>
      </div>
      <div style="font-family:var(--font-display);font-size:17px;font-weight:600;margin:9px 0 2px;">${escape(t.title)}</div>
      <div style="font-size:12px;font-weight:500;opacity:0.92;">
        ${t.question_count} Qs${t.entry_fee_kobo ? ` · entry ${escape(formatKobo(t.entry_fee_kobo))}` : ' · free'}
      </div>
      ${topicChips(t)}
      <div style="font-family:var(--font-display);color:#FFE9A6;font-size:19px;font-weight:700;margin-top:8px;">
        ${escape(formatKobo(t.prize_kobo))} pool
      </div>
      ${ctaHtml}
    </div>
  `;
}

function renderUpcoming(t) {
  const cd = t.scheduled_at
    ? `<span class="tag warn" data-countdown-to="${escape(t.scheduled_at)}">${escape(fmtCountdown(t.scheduled_at))}</span>`
    : '';
  const subline = `
    ${escape(fmtTime(t.scheduled_at))} ·
    ${escape(formatKobo(t.prize_kobo))} ·
    ${t.participants_count} joined
  `;
  if (t.is_registered) {
    return `
      <div class="card" style="padding:12px;">
        <div class="row">
          <div class="grow">
            <b style="font-size:13px;color:#fff;font-weight:600;">${escape(t.title)}</b>
            <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:1px;">
              ${subline}
            </div>
            ${topicChips(t)}
          </div>
          <div style="display:flex;flex-direction:column;gap:6px;align-items:flex-end;">
            <a href="/live/${escape(t.id)}" data-link class="tag ok" style="font-family:var(--font-display);text-decoration:none;">
              Joined ✓
            </a>
            ${cd}
          </div>
        </div>
      </div>
    `;
  }
  return `
    <div class="card" style="padding:12px;">
      <div class="row">
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">${escape(t.title)}</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:1px;">
            ${subline}
          </div>
          ${topicChips(t)}
        </div>
        <div style="display:flex;flex-direction:column;gap:6px;align-items:flex-end;">
          <a href="/live/${escape(t.id)}" data-link class="tag mag"
             style="text-decoration:none;font-family:var(--font-display);cursor:pointer;">
            Join
          </a>
          ${cd}
        </div>
      </div>
    </div>
  `;
}
